// src/views/BOM/ProcurementDetailPanel.js
import React, { useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Grid,
    Paper,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Button,
} from '@mui/material';
import {
    ReplayIcon,
    WarehouseIcon,
    StraightenIcon,
    CircleIcon,
    InfoIcon,
    AddCircleOutlineIcon,
    RemoveCircleOutlineIcon,
    SortIcon,
    CheckCircleIcon,
} from 'assets/constants/Icons';
import { BOM_STAGES } from 'assets/constants/Constants';
import './bom.css';

const ProcurementDetailPanel = React.memo(({
    rowData,
    quantity,
    virtualInventory,
    originalInventory,
    procurementDetails,
    onSelectionChange,
    isLoading,
    error,
    selectedOd,
    onSelectedOdChange,
    onRevertAll,
    currentStage,
    itemStage
}) => {
    const {
        raw_material_length_mm: lengthPerUnit,
        steel_material_description: material,
        raw_material_od_mm: initialOd,
        procurement_done: isProcured,
    } = rowData;

    // const isInProcurementStage = currentStage === BOM_STAGES.PROCUREMENT;
    const canAllocate = [
        BOM_STAGES.PROCUREMENT,
        BOM_STAGES.MANUFACTURING_PLANNING,
        BOM_STAGES.MANUFACTURING_EXECUTION
    ].includes(currentStage);
    const isRowInProcurement = (itemStage || 'Procurement') === BOM_STAGES.PROCUREMENT;


    const availableOds = useMemo(() => {
        if (!originalInventory || originalInventory.length === 0) {
            return initialOd ? [initialOd] : [];
        }

        const relevantInventory = originalInventory.filter(
            part => part.steel_material_description === material
        );

        const odSet = new Set(relevantInventory.map(part => part.od_mm));

        // if (initialOd) {
        //     odSet.add(initialOd);
        // }

        return Array.from(odSet).sort((a, b) => a - b);
    }, [originalInventory, initialOd, material]); 


    useEffect(() => {
        if (availableOds.length > 0 && !availableOds.includes(selectedOd)) {
            onSelectedOdChange(availableOds[0]);
        }
    }, [selectedOd, availableOds, onSelectedOdChange]);


    const { unitsAllocated, remainingUnits } = useMemo(() => {
        const totalLen = procurementDetails.reduce((sum, detail) => sum + detail.length_allocated, 0);
        const allocated = lengthPerUnit > 0 ? Math.floor(totalLen / lengthPerUnit) : 0;
        return {
            unitsAllocated: allocated,
            remainingUnits: quantity - allocated,
        };
    }, [procurementDetails, lengthPerUnit, quantity]);

    const allocationsMap = useMemo(() => {
        const allocations = new Map();
        procurementDetails.forEach(d => {
            const units = lengthPerUnit > 0 ? Math.floor(d.length_allocated / lengthPerUnit) : 0;
            allocations.set(d.raw_material_id, {
                allocatedLength: d.length_allocated,
                allocatedUnits: units
            });
        });
        return allocations;
    }, [procurementDetails, lengthPerUnit]);

    const filteredInventory = useMemo(() => {
        return virtualInventory.filter(part => part.od_mm === selectedOd && part.steel_material_description === material);
    }, [virtualInventory, selectedOd, material]);

    const handleAllocationChange = (inventoryPart, change) => {
        const allocationInfo = allocationsMap.get(inventoryPart._id) || { allocatedLength: 0 };
        const currentlyAvailableLength = inventoryPart.remaining_length_mm - allocationInfo.allocatedLength;
    
        if (change === 1 && remainingUnits <= 0) return;
        if (change === 1 && currentlyAvailableLength < lengthPerUnit) return;

        const existingAllocation = procurementDetails.find(d => d.raw_material_id === inventoryPart._id);
        let newProcurementDetails;

        if (existingAllocation) {
            const newAllocatedLength = existingAllocation.length_allocated + (change * lengthPerUnit);
            if (newAllocatedLength > 0) {
                newProcurementDetails = procurementDetails.map(d =>
                    d.raw_material_id === inventoryPart._id
                        ? { ...d, length_allocated: newAllocatedLength }
                        : d
                );
            } else {
                newProcurementDetails = procurementDetails.filter(d => d.raw_material_id !== inventoryPart._id);
            }
        } else if (change === 1) {
            newProcurementDetails = [
                ...procurementDetails,
                { raw_material_id: inventoryPart._id, length_allocated: lengthPerUnit }
            ];
        } else {
            return;
        }

        if (onSelectionChange) {
            onSelectionChange(newProcurementDetails);
        }
    };

    const renderInventoryList = () => {
        if (isLoading) return <Typography>🔍 Searching inventory...</Typography>;
        if (error) return <Typography color="error">⚠️ Error: {error}</Typography>;
        const showCompletedView = isProcured || !canAllocate || !isRowInProcurement;
        if (showCompletedView) {
            const allocatedPartIds = new Set(procurementDetails.map(d => d.raw_material_id));
            const allocatedParts = originalInventory.filter(part => allocatedPartIds.has(part._id));

            if (allocatedParts.length === 0) {
                return <Typography>✅ Procurement is marked complete, but no specific raw materials are linked.</Typography>;
            }

            return (
                <Box className="procurementdetailpanel-scroll" sx={{ flexGrow: 1 }}> 
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: 'text.secondary' }}>
                            Completed Allocation:
                        </Typography>
                    {currentStage === BOM_STAGES.PROCUREMENT && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<ReplayIcon />}
                            onClick={onRevertAll}
                        >
                            Revert All
                        </Button>
                    )}
                    </Box>
                    <List dense className="procurementdetailpanel-list">
                        {allocatedParts.map((part) => {
                            const allocationDetail = procurementDetails.find(d => d.raw_material_id === part._id);
                            const allocatedLength = allocationDetail ? allocationDetail.length_allocated : 0;
                            return (
                                <ListItem
                                    key={part._id}
                                    className="procurementdetailpanel-item procurementdetailpanel-item-compact"
                                    sx={{
                                        bgcolor: '#F8F8F8', color: '#333333', borderRadius: 1,
                                        borderLeft: '5px solid', borderColor: (theme) => theme.palette.primary.main,
                                    }}
                                >
                                    <ListItemIcon className="procurementdetailpanel-icon">
                                        <CheckCircleIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={part.part_name}
                                        secondary={`Allocated Length: ${allocatedLength} mm | OD: ${part.od_mm}`}
                                        primaryTypographyProps={{ fontWeight: 'bold', sx: { fontSize: '12px' } }}
                                        secondaryTypographyProps={{ color: 'inherit', opacity: 0.85, sx: { fontSize: '12px' } }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            );
        }

        if (filteredInventory.length === 0) {
            return <Typography>ℹ️ No matching raw materials found for the selected OD and Material.</Typography>;
        }

        return (
            <Box className="procurementdetailpanel-scroll" sx={{ flexGrow: 1 }}>
                <List dense className="procurementdetailpanel-list">
                    {filteredInventory.map((part) => {
                        const allocationInfo = allocationsMap.get(part._id) || { allocatedLength: 0, allocatedUnits: 0 };
                        const unitsFromThisPart = allocationInfo.allocatedUnits;
                        const originalPart = originalInventory.find(p => p._id === part._id);
                        const currentlyAvailableLength = part.remaining_length_mm - allocationInfo.allocatedLength;
                        const isAddDisabled = remainingUnits <= 0 || currentlyAvailableLength < lengthPerUnit;

                        return (
                            <ListItem
                                key={part._id}
                                className="procurementdetailpanel-item procurementdetailpanel-item-compact-sm"
                                secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleAllocationChange(part, -1)}
                                            disabled={unitsFromThisPart === 0}
                                            color="warning"
                                        >
                                            <RemoveCircleOutlineIcon />
                                        </IconButton>
                                        <Typography sx={{ fontWeight: 'bold', minWidth: '2ch', textAlign: 'center', fontSize: '12px' }}>
                                            {unitsFromThisPart}
                                        </Typography>
                                        <IconButton 
                                            size="small"
                                            onClick={() => handleAllocationChange(part, 1)}
                                            disabled={isAddDisabled}
                                            color="success">
                                            <AddCircleOutlineIcon />
                                        </IconButton>
                                    </Box>
                                }
                                sx={{ bgcolor: unitsFromThisPart > 0 ? 'action.hover' : 'transparent', borderRadius: 1 }}
                            >
                                <ListItemIcon className="procurementdetailpanel-icon"><CircleIcon sx={{ fontSize: 12, color: part.remaining_length_mm > lengthPerUnit ? 'success.main' : 'error.main' }} /></ListItemIcon>
                                <ListItemText
                                    primary={`Available: ${currentlyAvailableLength}mm`}
                                    secondary={`Stock Remaining: ${originalPart?.remaining_length_mm || 0}mm | OD: ${part.od_mm}`}
                                    primaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>Required Material</Typography>
                        <Divider sx={{ my: 1 }} />
                        <List dense className="procurementdetailpanel-list">
                            <ListItem className="procurementdetailpanel-item procurementdetailpanel-item-compact">
                                <ListItemIcon className="procurementdetailpanel-icon"><StraightenIcon color="primary" /></ListItemIcon>
                                <ListItemText 
                                    primary="Required per Unit" 
                                    secondary={`${lengthPerUnit || 0} mm`} 
                                    primaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                />
                            </ListItem>
                            <ListItem className="procurementdetailpanel-item procurementdetailpanel-item-compact">
                                <ListItemIcon className="procurementdetailpanel-icon"><SortIcon color="primary" /></ListItemIcon>
                                <ListItemText 
                                    primary="Total Quantity Required" 
                                    secondary={`${quantity || 0} units`} 
                                    primaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                />
                            </ListItem>
                            <ListItem className="procurementdetailpanel-item procurementdetailpanel-item-compact">
                                <ListItemIcon className="procurementdetailpanel-icon"><CheckCircleIcon color={remainingUnits <= 0 ? "success" : "warning"} /></ListItemIcon>
                                <ListItemText
                                    primary="Units Allocated"
                                    secondary={`${unitsAllocated} / ${quantity}`}
                                    secondaryTypographyProps={{ color: remainingUnits <= 0 ? "success.main" : "warning.main", fontWeight: 'bold', sx: { fontSize: '12px' } }}
                                    primaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                />
                            </ListItem>
                            <ListItem className="procurementdetailpanel-item">
                                <ListItemIcon className="procurementdetailpanel-icon"><InfoIcon color="primary" /></ListItemIcon>
                                <ListItemText 
                                    primary="Material Type" 
                                    secondary={material || 'N/A'} 
                                    primaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '12px' } }}
                                />
                            </ListItem>
                            <ListItem className="procurementdetailpanel-item">
                                <ListItemIcon className="procurementdetailpanel-icon"><CircleIcon color="primary" sx={{ fontSize: 16 }} /></ListItemIcon>
                                <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
                                    <ListItemText primary="Select OD to Filter" primaryTypographyProps={{ sx: { fontSize: '12px' } }} />
                                        <Select 
                                            value={selectedOd || ''}
                                            onChange={(e) => onSelectedOdChange(Number(e.target.value))}
                                        >
                                            {availableOds.map(odValue => (
                                                <MenuItem key={odValue} value={odValue} sx={{ fontSize: '12px' }}>{odValue} mm</MenuItem>
                                            ))}
                                        </Select>
                                </FormControl>
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                            <WarehouseIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Available Inventory (Filtered by OD: {selectedOd} mm)
                        </Typography>
                        <Divider sx={{ mb: 0.5 }} />
                        {renderInventoryList()}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
});

export default ProcurementDetailPanel;